import { FC, FormEvent, ReactNode } from 'react';
import { Modal } from '../ui/Modal/Modal';

interface FormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit?: (e: FormEvent) => void;
  title: string;
  children: ReactNode;
  submitText?: string;
  cancelText?: string;
  isLoading?: boolean;
  size?: 'sm' | 'md' | 'lg' | 'xl';
}

export const FormModal: FC<FormModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  title,
  children,
  submitText = 'Save',
  cancelText = 'Cancel',
  isLoading = false,
  size = 'md',
}) => {
  // When both submitText and cancelText are empty, the child component
  // is expected to handle its own form and buttons (e.g., ServiceForm).
  // In this case, we skip the form wrapper to avoid nested <form> tags.
  const hasButtons = submitText !== '' || cancelText !== '';

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    onSubmit?.(e);
  };

  // Content without form wrapper - for when child has its own form
  if (!hasButtons) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title={title} size={size} closable={!isLoading}>
        <div className="space-y-4">{children}</div>
      </Modal>
    );
  }

  // Content with form wrapper - standard FormModal behavior
  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size={size} closable={!isLoading}>
      <form onSubmit={handleSubmit}>
        <div className="space-y-4">{children}</div>
        <div className="mt-6 flex justify-end gap-3">
          {cancelText && (
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-4 py-2 text-sm font-medium text-gray-700 dark:text-slate-300 shadow-sm hover:bg-gray-50 dark:hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {cancelText}
            </button>
          )}
          {submitText && (
            <button
              type="submit"
              disabled={isLoading}
              className="inline-flex items-center rounded-md border border-transparent bg-blue-600 dark:bg-blue-500 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 dark:hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isLoading ? (
                <>
                  <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  Saving...
                </>
              ) : (
                submitText
              )}
            </button>
          )}
        </div>
      </form>
    </Modal>
  );
};
